/**
 * MI DINERO › DÓNDE LO GUARDAS — administrar cuentas.
 *
 * Esta lista vivía dentro de PERFIL. Que lo más central del producto —dónde
 * está tu dinero— estuviera en el cajón de configuración es la razón por la
 * que el Home viejo tenía que mandarte ahí con un botón que decía «Gestionar».
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, humanErrorLine } from "@/lib/human";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ChevronLeft, Wallet, Banknote, Home } from "lucide-react";
import { AddAccountDialog, EditAccountDialog } from "@/components/AccountDialogs";

export default function AccountsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setAccounts((await api.get("/accounts")).data || []); }
    catch (e) { toast.error(humanErrorLine(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("td:data-changed", load);
    return () => window.removeEventListener("td:data-changed", load);
  }, [load]);

  const remove = async (a) => {
    if (!window.confirm(
      `¿Borrar «${a.name}»?\n\nSe van a borrar también todos los movimientos que anotaste en ella. Esto no se puede deshacer.`
    )) return;
    try { await api.delete(`/accounts/${a.id}`); toast.success("Cuenta borrada"); load(); }
    catch (e) { toast.error(humanErrorLine(e)); }
  };

  return (
    <div className="space-y-6 pb-4" data-testid="accounts-page">
      <header className="flex items-center gap-2">
        <button onClick={() => nav("/mi-dinero")} aria-label="Volver a Mi dinero"
          className="p-2 -ml-2 rounded-xl text-slate-300 hover:bg-white/10 min-h-11 min-w-11 flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-white">Dónde guardas tu dinero</h1>
      </header>

      {accounts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="accounts-empty">
          <p className="text-[16px] font-semibold text-white">Todavía no has agregado nada.</p>
          <p className="text-[14px] text-slate-400 mt-2 leading-relaxed max-w-sm mx-auto">
            Agrega tu cuenta del banco, o el efectivo que cargas encima. Es lo primero:
            sin esto la app no sabe de dónde sale tu dinero.
          </p>
          <button onClick={() => setAdding(true)} data-testid="accounts-empty-cta"
            className="mt-5 inline-flex items-center gap-2 px-5 min-h-12 rounded-2xl bg-white text-[#15141b] font-bold">
            <Plus className="w-4 h-4" aria-hidden="true" /> Agregar la primera
          </button>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {accounts.map(a => (
              <li key={a.id} className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 min-h-[72px]"
                data-testid={`account-row-${a.id}`}>
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${a.color || "#8B5CF6"}22` }}>
                  {a.type === "cash"
                    ? <Banknote className="w-[18px] h-[18px] text-slate-200" aria-hidden="true" />
                    : <Wallet className="w-[18px] h-[18px] text-slate-200" aria-hidden="true" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-white truncate">{a.name}</span>
                    {a.is_shared && (
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-violet-300 border border-violet-500/30 rounded px-1.5 py-0.5 shrink-0">
                        <Home className="w-2.5 h-2.5" aria-hidden="true" /> familia
                      </span>
                    )}
                  </span>
                  <span className="block text-[15px] font-bold text-white tabular-nums mt-0.5">
                    {money(a.balance, a.currency)}
                  </span>
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(a)} data-testid={`edit-account-${a.id}`}
                    aria-label={`Editar ${a.name}`}
                    className="p-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 min-h-11 min-w-11 flex items-center justify-center">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(a)} data-testid={`delete-account-${a.id}`}
                    aria-label={`Borrar ${a.name}`}
                    className="p-2.5 rounded-xl text-slate-300 hover:text-rose-300 hover:bg-rose-500/10 min-h-11 min-w-11 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <button onClick={() => setAdding(true)} data-testid="accounts-add"
            className="w-full min-h-12 rounded-2xl border border-dashed border-white/20 text-[15px] font-semibold text-slate-300 hover:bg-white/[0.04] flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" aria-hidden="true" /> Agregar otra
          </button>
        </>
      )}

      <AddAccountDialog open={adding} onOpenChange={setAdding} onCreated={load}
        defaultCurrency={user?.country_currency || "DOP"} effectiveTier={user?.effective_tier} />
      <EditAccountDialog account={editing} onClose={() => setEditing(null)} onSaved={load}
        effectiveTier={user?.effective_tier} />
    </div>
  );
}
